import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Nature,
  CloudQueue,
  AccountBalance,
  GraphicEq,
  // Psychology,
  Visibility,
  Lock,
  ShowChart,
  Search,
  QueryStats,
  FlashOn,
  VerifiedUser,
} from '@mui/icons-material';
import { motion, Variants } from 'motion/react';
import { FeatureShowcaseScroll } from 'src/view/components/FeatureShowcaseScroll';
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  alpha,
} from '@mui/material';
import IntelligenceBackground from 'src/view/components/Background/IntelligenceBackground';
import Footer from 'src/view/components/navigation/Footer';

// Importing images from resources
import LogoWhite from 'src/resources/images/logo-white 1.png';
import NatureBg from 'src/resources/images/nature-bg.png';
import ClimateBg from 'src/resources/images/climate-bg.png';
import TnfdBg from 'src/resources/images/tnfd-bg.png';
import BioacousticBg from 'src/resources/images/bioacoustic-bg.png';
// import FloraBg from 'src/resources/images/flora-bg.png';
import SpeciesBg from 'src/resources/images/species-bg.png';
import TcfdBg from 'src/resources/images/tcfd-bg.png';

const COLORS = {
  primaryGreen: '#00f296',
  secondaryGreen: '#0a4d3c',
  darkBg: '#040b08',
  cardBg: 'rgba(6, 18, 12, 0.7)',
  glassBorder: 'rgba(0, 242, 150, 0.15)',
  textMain: '#ffffff',
  textDim: '#a0b0a8',
};

const revealVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.12,
      type: 'spring' as const,
      stiffness: 70,
      damping: 20,
    },
  }),
};

const ToolHub: React.FC = () => {
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/auth');
  };

  const cardStyles = (bgImage?: string) => ({
    background: bgImage
      ? `linear-gradient(to top, rgba(10, 25, 18, 0.95), transparent), url(${bgImage})`
      : 'rgba(10, 25, 18, 0.4)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(COLORS.primaryGreen, 0.1)}`,
    borderRadius: '4px',
    padding: '22px',
    position: 'relative',
    overflow: 'hidden',
    height: '260px',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      borderColor: alpha(COLORS.primaryGreen, 0.4),
      transform: 'translateY(-5px)',
      boxShadow: `0 0 30px ${alpha(COLORS.primaryGreen, 0.2)}`,
      background: bgImage
        ? `linear-gradient(to top, rgba(10, 25, 18, 0.95), rgba(10, 25, 18, 0.1)), url(${bgImage})`
        : 'rgba(10, 25, 18, 0.5)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: `radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), ${alpha(COLORS.primaryGreen, 0.1)} 0%, transparent 70%)`,
      opacity: 0,
      transition: 'opacity 0.3s',
      pointerEvents: 'none',
    },
    '&:hover::before': {
      opacity: 1,
    }
  });

  const lockedCardStyles = (bgImage?: string) => ({
    ...cardStyles(bgImage),
    opacity: 0.6,
    filter: 'grayscale(0.6)',
    '&:hover': {
      borderColor: alpha(COLORS.primaryGreen, 0.1),
      transform: 'none',
      boxShadow: 'none',
    },
  });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    card.style.setProperty('--mouse-x', `${x}px`);
    card.style.setProperty('--mouse-y', `${y}px`);
  };

  const unlockBtnStyles = {
    background: 'rgba(10, 30, 20, 0.82)',
    border: `1px solid ${alpha(COLORS.primaryGreen, 0.2)}`,
    color: COLORS.primaryGreen,
    padding: '8px 14px',
    borderRadius: '4px',
    fontSize: '0.72rem',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    width: '100%',
    marginTop: 'auto',
    '&:hover': {
      background: alpha(COLORS.primaryGreen, 0.15),
      borderColor: COLORS.primaryGreen,
      boxShadow: `0 0 15px ${alpha(COLORS.primaryGreen, 0.1)}`,
    },
    '&:disabled': {
      opacity: 0.5,
      color: COLORS.primaryGreen,
      borderColor: alpha(COLORS.primaryGreen, 0.2),
    },
  };


  return (
    <Box
      sx={{
        bgcolor: COLORS.darkBg,
        color: COLORS.textMain,
        minHeight: '100vh',
        fontFamily: '"Inter", sans-serif',
        overflowX: 'hidden',
        position: 'relative',
      }}
    >
      <IntelligenceBackground />
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <Container maxWidth={false} sx={{ maxWidth: '1600px', px: { xs: '20px', md: '60px' }, pt: '80px', pb: '20px' }}>

          {/* Header */}
          <Box
            component="header"
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: '70px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: { xs: '20px', md: '80px' },
              zIndex: 1000,
              background: 'rgba(4, 11, 8, 0.85)',
              backdropFilter: 'blur(15px)',
              maxWidth: '1600px',
              mx: 'auto',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src={LogoWhite} alt="Darukaa.earth" style={{ height: '24px', objectFit: 'contain' }} />
            </Box>

            <Button
              onClick={handleLogin}
              sx={{
                color: COLORS.primaryGreen,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { background: 'transparent', opacity: 0.8 },
              }}
            >
              Login
            </Button>
          </Box>

          {/* Hero Section */}
          <Box component="section" sx={{ textAlign: 'left', mt: '10px', mb: '25px' }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.8rem' },
                fontWeight: 800,
                mb: '5px',
                letterSpacing: '-0.5px',
                textTransform: 'uppercase',
                lineHeight: 1.1,
              }}
            >
              ENVIRONMENTAL INTELLIGENCE
            </Typography>
            <Typography
              sx={{
                color: COLORS.textDim,
                fontSize: '1rem',
                maxWidth: '500px',
                lineHeight: 1.4,
                fontWeight: 400,
                mt: '10px',
              }}
            >
              Monitoring nature-related risk and climate impact across global assets.
            </Typography>
          </Box>

          {/* Main Tool Hub Content Area */}
          <Box sx={{ mt: '20px', mb: '60px' }}>
            <Grid container spacing={2.5}>
              {/* Top Row */}
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.2 }}
                  custom={0}
                  variants={revealVariants}
                >
                  <Box sx={lockedCardStyles(NatureBg)} onMouseMove={handleMouseMove}>
                    <Nature sx={{ color: COLORS.primaryGreen, mb: '12px', fontSize: '1.3rem', opacity: 0.85 }} />
                    <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 700, mb: '6px', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.2 }}>
                      NATURE<br />METRICS
                    </Typography>
                    <Box sx={{ borderLeft: `2px solid ${alpha(COLORS.primaryGreen, 0.4)}`, pl: '12px', mb: '8px' }}>
                      <Typography sx={{ color: COLORS.textDim, fontSize: '0.78rem', lineHeight: 1.4 }}>
                        Quantifiable indicators of ecological health, soil, and biomass.
                      </Typography>
                    </Box>
                    <Button sx={unlockBtnStyles} disabled endIcon={<Lock fontSize="small" />}>
                      LOCKED
                    </Button>
                  </Box>
                </motion.div>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.2 }}
                  custom={1}
                  variants={revealVariants}
                >
                  <Box sx={lockedCardStyles(ClimateBg)} onMouseMove={handleMouseMove}>
                    <CloudQueue sx={{ color: COLORS.primaryGreen, mb: '12px', fontSize: '1.3rem', opacity: 0.85 }} />
                    <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 700, mb: '6px', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.2 }}>
                      CLIMATE INTELLIGENCE
                    </Typography>
                    <Box sx={{ borderLeft: `2px solid ${alpha(COLORS.primaryGreen, 0.4)}`, pl: '12px', mb: '8px' }}>
                      <Typography sx={{ color: COLORS.textDim, fontSize: '0.78rem', lineHeight: 1.4, textTransform: 'uppercase' }}>
                        Monitor climate variability, ecosystem stress, and environmental risk through real time intelligence.
                      </Typography>
                    </Box>
                    <Button sx={unlockBtnStyles} disabled endIcon={<Lock fontSize="small" />}>
                      LOCKED
                    </Button>
                  </Box>
                </motion.div>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.2 }}
                  custom={2}
                  variants={revealVariants}
                >
                  <Box sx={lockedCardStyles(TcfdBg)} onMouseMove={handleMouseMove}>
                    <ShowChart sx={{ color: COLORS.primaryGreen, mb: '12px', fontSize: '1.3rem', opacity: 0.85 }} />
                    <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 700, mb: '6px', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.2 }}>
                      TCFD COMPLIANCE
                    </Typography>
                    <Box sx={{ borderLeft: `2px solid ${alpha(COLORS.primaryGreen, 0.4)}`, pl: '12px', mb: '8px' }}>
                      <Typography sx={{ color: COLORS.textDim, fontSize: '0.78rem', lineHeight: 1.4 }}>
                        Financial impact analysis of climate-related risks and opportunities.
                      </Typography>
                    </Box>
                    <Button sx={unlockBtnStyles} disabled endIcon={<Lock fontSize="small" />}>
                      LOCKED
                    </Button>
                  </Box>
                </motion.div>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.2 }}
                  custom={3}
                  variants={revealVariants}
                >
                  <Box sx={lockedCardStyles(TnfdBg)} onMouseMove={handleMouseMove}>
                    <AccountBalance sx={{ color: COLORS.primaryGreen, mb: '12px', fontSize: '1.3rem', opacity: 0.85 }} />
                    <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 700, mb: '6px', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.2 }}>
                      TNFD<br />ANALYTICS
                    </Typography>
                    <Box sx={{ borderLeft: `2px solid ${alpha(COLORS.primaryGreen, 0.4)}`, pl: '12px', mb: '8px' }}>
                      <Typography sx={{ color: COLORS.textDim, fontSize: '0.78rem', lineHeight: 1.4 }}>
                        Taskforce on Nature-related Financial Disclosures metrics.
                      </Typography>
                    </Box>
                    <Button sx={unlockBtnStyles} disabled endIcon={<Lock fontSize="small" />}>
                      LOCKED
                    </Button>
                  </Box>
                </motion.div>
              </Grid>

              {/* Darukaa Models Sub-Header */}
              <Grid item xs={12} sx={{ mt: '30px', mb: '15px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '6px' }}>
                  <Typography variant="h2" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                    Darukaa Models
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: alpha(COLORS.primaryGreen, 0.1),
                      color: COLORS.primaryGreen,
                      border: `1px solid ${alpha(COLORS.primaryGreen, 0.2)}`,
                      px: '6px',
                      py: '2px',
                      borderRadius: '4px',
                      fontSize: '0.6rem',
                      fontWeight: 700,
                    }}
                  >
                    AI
                  </Box>
                </Box>
                <Typography sx={{ color: COLORS.textDim, fontSize: '0.85rem', maxWidth: '650px', opacity: 0.8 }}>
                  AI-powered ecological intelligence models for biodiversity and ecosystem monitoring.
                </Typography>
              </Grid>

              {/* Models Row */}
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.2 }}
                  custom={0}
                  variants={revealVariants}
                >
                  <Box sx={cardStyles(BioacousticBg)} onMouseMove={handleMouseMove}>
                    <GraphicEq sx={{ color: COLORS.primaryGreen, mb: '12px', fontSize: '1.3rem', opacity: 0.85 }} />
                    <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 700, mb: '6px', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.2 }}>
                      BIOACOUSTICS
                    </Typography>
                    <Box sx={{ borderLeft: `2px solid ${alpha(COLORS.primaryGreen, 0.4)}`, pl: '12px', mb: '8px' }}>
                      <Typography sx={{ color: COLORS.textDim, fontSize: '0.78rem', lineHeight: 1.4 }}>
                        Detect and identify wildlife sounds and bird species.
                      </Typography>
                    </Box>
                    <Button sx={unlockBtnStyles} onClick={handleLogin} endIcon={<Lock fontSize="small" />}>
                      LOGIN TO UNLOCK
                    </Button>
                  </Box>
                </motion.div>
              </Grid>

              {/* Flora Intelligence is hidden for now.
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.2 }}
                  custom={1}
                  variants={revealVariants}
                >
                  <Box sx={lockedCardStyles(FloraBg)} onMouseMove={handleMouseMove}>
                    <Psychology sx={{ color: COLORS.primaryGreen, mb: '12px', fontSize: '1.3rem', opacity: 0.85 }} />
                    <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 700, mb: '6px', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.2 }}>
                      FLORA<br />INTELLIGENCE
                    </Typography>
                    <Box sx={{ borderLeft: `2px solid ${alpha(COLORS.primaryGreen, 0.4)}`, pl: '12px', mb: '8px' }}>
                      <Typography sx={{ color: COLORS.textDim, fontSize: '0.78rem', lineHeight: 1.4 }}>
                        Identify plant species and assess vegetation health.
                      </Typography>
                    </Box>
                    <Button sx={unlockBtnStyles} disabled endIcon={<Lock fontSize="small" />}>
                      LOCKED
                    </Button>
                  </Box>
                </motion.div>
              </Grid> */}

              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.2 }}
                  custom={2}
                  variants={revealVariants}
                >
                  <Box sx={lockedCardStyles(SpeciesBg)} onMouseMove={handleMouseMove}>
                    <Visibility sx={{ color: COLORS.primaryGreen, mb: '12px', fontSize: '1.3rem', opacity: 0.85 }} />
                    <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 700, mb: '6px', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.2 }}>
                      SPECIES VISION
                    </Typography>
                    <Box sx={{ borderLeft: `2px solid ${alpha(COLORS.primaryGreen, 0.4)}`, pl: '12px', mb: '8px' }}>
                      <Typography sx={{ color: COLORS.textDim, fontSize: '0.78rem', lineHeight: 1.4 }}>
                        Recognize species using advanced computer vision.
                      </Typography>
                    </Box>
                    <Button sx={unlockBtnStyles} disabled endIcon={<Lock fontSize="small" />}>
                      LOCKED
                    </Button>
                  </Box>
                </motion.div>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.2 }}
                  custom={3}
                  variants={revealVariants}
                >
                  <Box sx={{ ...cardStyles(), border: `1px dashed ${alpha(COLORS.primaryGreen, 0.2)}`, background: 'rgba(10, 25, 18, 0.2)' }} onMouseMove={handleMouseMove}>
                    <Lock sx={{ color: COLORS.primaryGreen, mb: '12px', fontSize: '1.3rem', opacity: 0.85 }} />
                    <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 700, mb: '6px', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.2 }}>
                      EDNA
                    </Typography>
                    <Typography sx={{ color: COLORS.primaryGreen, fontWeight: 800, fontSize: '0.75rem', mt: 'auto', mb: '10px', letterSpacing: '1px' }}>
                      UNDER DEVELOPMENT<br />COMING SOON.....
                    </Typography>
                    <Button sx={unlockBtnStyles} disabled endIcon={<Lock fontSize="small" />}>
                      LOCKED
                    </Button>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Box>

          {/* How It Works Section */}
          <Box
            component="section"
            sx={{
              width: '100vw',
              marginLeft: 'calc(-50vw + 50%)',
              bgcolor: '#000',
              position: 'relative',
              zIndex: 10,
              mt: '80px',
              pt: '60px',
              pb: '60px',
              textAlign: 'center',
              boxShadow: '0 -100px 100px rgba(4, 11, 8, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-150px',
                left: 0,
                width: '100%',
                height: '150px',
                background: 'linear-gradient(to bottom, transparent, #000)',
                pointerEvents: 'none',
              },
            }}
          >
            <Typography variant="h2" sx={{ fontSize: '2.8rem', fontWeight: 800, mb: '50px', letterSpacing: '-0.02em' }}>
              How It Works
            </Typography>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, margin: "-100px" }}
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.15,
                  },
                },
              }}
            >
              <Grid container spacing={5} sx={{ maxWidth: '1300px', mx: 'auto', px: '40px', position: 'relative' }}>
                {[
                  { icon: <Search />, title: 'Explore', desc: 'Identify critical regions and ecosystems requiring stewardship.' },
                  { icon: <QueryStats />, title: 'Analyze', desc: 'Leverage our data models to forecast environmental shifts.' },
                  { icon: <FlashOn />, title: 'Act', desc: 'Implement science-backed strategies for intervention.' },
                  { icon: <VerifiedUser />, title: 'Impact', desc: 'Monitor and report real-world outcomes to stakeholders.' },
                ].map((step, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <motion.div
                      variants={{
                        hidden: { opacity: 0, y: 30, scale: 0.9 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          scale: 1,
                          transition: {
                            type: 'spring',
                            damping: 12,
                            stiffness: 100
                          }
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            background: 'rgba(0, 242, 150, 0.05)',
                            border: `1px solid ${alpha(COLORS.primaryGreen, 0.15)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: COLORS.primaryGreen,
                            mb: '18px',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              background: 'rgba(0, 242, 150, 0.15)',
                              transform: 'translateY(-5px) scale(1.05)',
                              boxShadow: '0 10px 30px rgba(0, 242, 150, 0.2)',
                              borderColor: COLORS.primaryGreen,
                            },
                          }}
                        >
                          {step.icon}
                        </Box>
                        <Typography variant="h4" sx={{ fontSize: '0.95rem', fontWeight: 700, mb: '8px', letterSpacing: '0.5px' }}>
                          {step.title}
                        </Typography>
                        <Typography sx={{ color: COLORS.textDim, fontSize: '0.78rem', lineHeight: 1.5, maxWidth: '180px', opacity: 0.8 }}>
                          {step.desc}
                        </Typography>
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Box>

          {/* Integrated Dynamic Feature Showcase */}
          <Box component="section" sx={{ width: '100vw', marginLeft: 'calc(-50vw + 50%)', position: 'relative', zIndex: 10 }}>
            <FeatureShowcaseScroll />
          </Box>

          {/* Footer */}
          <Box sx={{ width: '100vw', marginLeft: 'calc(-50vw + 50%)', position: 'relative', zIndex: 10 }}>
            <Footer />
          </Box>

        </Container>
      </Box>
    </Box>
  );
};

export default ToolHub;
